import adminApi from "../api/admin/adminApi";
import {
  AGENCY_STATUSES,
  Agency,
  AgencyStatus,
  Run,
  Dict,
  DictDTO,
  AgencyRunTotal,
  AgencyUsersInfo,
  GetUsersInfoResponse,
  UserSearchResult,
  AdminWorkflowDTO,
  UserExportDTO,
  SalesMetricDTO,
  IssueCategoryDTO,
  IssueDTO,
  CategoryHistoryEntryDTO,
  CollectIssuesResponse,
  RecalculateCategoriesResponse,
  UsageComparisonParams,
  PolicyInsightsParams,
  PolicyInsightsResponseDTO,
  UsageIterationsParams,
  UsageOverviewParams,
  UsageOverviewResponseDTO,
  WorkflowIterationsResponseDTO,
  UsageComparisonResponseDTO,
  UsageTimeseriesParams,
  UsageTimeseriesResponseDTO,
  PrDashboardResponse,
  OperatingCostEntry,
  OperatingCostsResponse,
} from "../api/admin/types";

const parseAgencyStatus = (
  status: string | null | undefined
): { status: AgencyStatus | null; unknownStatus: string | null } => {
  if (!status) return { status: null, unknownStatus: null };
  if ((AGENCY_STATUSES as readonly string[]).includes(status)) {
    return { status: status as AgencyStatus, unknownStatus: null };
  }
  console.warn(
    `[AdminController] Unrecognized agency status "${status}"; treating as unset. ` +
      "The backend may support a status value this frontend enum doesn't know about yet."
  );
  return { status: null, unknownStatus: status };
};

// Free-mail/internal domains that don't identify an agency; kept "Unnamed Agency".
const NON_AGENCY_LABELS = new Set([
  "gmail",
  "yahoo",
  "hotmail",
  "outlook",
  "icloud",
  "aol",
  "coverflow",
]);

// Compound TLDs (co.uk, com.br, ...) push the registrable label one level left.
const COMPOUND_TLD_LABELS = new Set(["co", "com", "org", "net", "gov", "edu", "ac"]);

// Readable name from an owner's email domain, e.g. "x@one-trustins.com" -> "One-trustins".
const nameFromOwnerEmail = (email: string | null | undefined): string => {
  const domain = email?.split("@")[1]?.toLowerCase();
  if (!domain) return "Unnamed Agency";
  const parts = domain.split(".");
  const index =
    parts.length >= 3 && COMPOUND_TLD_LABELS.has(parts[parts.length - 2])
      ? parts.length - 3
      : parts.length - 2;
  const label = parts[index];
  if (!label || NON_AGENCY_LABELS.has(label)) return "Unnamed Agency";
  return label.charAt(0).toUpperCase() + label.slice(1);
};

// Shared "no name" fallback so getAgencies/getUsage/getUsageExport agree on vocabulary.
const deriveAgencyDisplayName = ({
  name,
  ownerEmail,
  isSelfServeSignup,
}: {
  name?: string | null;
  ownerEmail?: string | null;
  isSelfServeSignup?: boolean;
}): string => {
  if (name) return name;
  return isSelfServeSignup ? nameFromOwnerEmail(ownerEmail) : "Unnamed Agency";
};

const formatUsageAgency = (agency: any): Dict => ({
  name: deriveAgencyDisplayName({ name: agency.name }),
  agencyId: agency.agency_id ?? "",
  userCount: agency.user_count ?? 0,
  users: agency.users,
  creationDate: agency.created_at ?? null,
  policiesCount: agency.policies_count ?? 0,
  comparisonsCount: agency.comparisons_count ?? 0,
  proposalsCount: agency.proposals_count ?? 0,
  contractReviewsCount: agency.contract_reviews_count ?? 0,
  gapAnalysisCount: agency.gap_analysis_count ?? 0,
  singlePolicyAnalysisCount: agency.single_policy_analysis_count ?? 0,
  sovCount: agency.sov_count ?? 0,
  lossRunsCount: agency.loss_runs_count ?? 0,
  coiCount: agency.coi_count ?? 0,
  chatsCount: agency.chats_count ?? 0,
  policiesCountLastWeek: agency.policies_count_last_week ?? 0,
  comparisonsCountLastWeek: agency.comparisons_count_last_week ?? 0,
  proposalsCountLastWeek: agency.proposals_count_last_week ?? 0,
  contractReviewsCountLastWeek: agency.contract_reviews_count_last_week ?? 0,
  gapAnalysisCountLastWeek: agency.gap_analysis_count_last_week ?? 0,
  singlePolicyAnalysisCountLastWeek: agency.single_policy_analysis_count_last_week ?? 0,
  sovCountLastWeek: agency.sov_count_last_week ?? 0,
  lossRunsCountLastWeek: agency.loss_runs_count_last_week ?? 0,
  coiCountLastWeek: agency.coi_count_last_week ?? 0,
  chatsCountLastWeek: agency.chats_count_last_week ?? 0,
});

const AdminController = {
  getAgencies: async (agencyId?: string | null): Promise<Agency[]> => {
    const response = await adminApi.adminGetAgencies(agencyId);

    const formatted = response.body.agencies.map((agency) => {
      const isSelfServeSignup = !agency.name && (agency.is_self_serviced ?? false);
      return {
        id: agency._id,
        agencyId: agency.agency_id,
        isActive: agency.active ?? true,
        isListed: agency.active === true,
        isSelfServeSignup,
        ...parseAgencyStatus(agency.status),
        monthlyFee: agency.monthly_fee ?? null,
        monthlyCredits: agency.monthly_credits ?? null,
        overageFee: agency.overage_fee ?? null,
        allowedUsers: agency.allowed_users ?? null,
        planType: agency.plan_type ?? null,
        name: deriveAgencyDisplayName({
          name: agency.name,
          ownerEmail: agency.owner_email,
          isSelfServeSignup,
        }),
        credits: agency.credits,
        maxCredits: agency.maxCredits,
      };
    });

    return formatted;
  },
  getAgencyRuns: async (agencyId: string): Promise<AgencyRunTotal> => {
    const agencyRuns = (await adminApi.adminGetAgencyRuns(agencyId)).body;

    const formatted = {
      runs: agencyRuns.all_report_runs
        .map((r) => {
          return {
            id: r.run_id,
            status: r.status,
            generationTime: new Date(r.generation_time),
            runId: r.run_id,
            instanceIds: r.instance_ids,
            user: {
              username: r.user.username,
              email: r.user.email,
            },
            result: {
              carrier: r.result?.carrier ?? "",
              definitions: r.result?.definitions ?? {},
              formSchedule: r.result?.form_schedule ?? "",
              keyInfo: r.result?.key_info ?? "",
              markdownStr: r.result?.markdown_str ?? "",
              policyPeriod: r.result?.policy_period ?? "",
              runTime: r.result?.run_time ?? 0,
              typeVals: r.result?.type_vals ?? [],
              types: r.result?.types ?? [],
            },
          };
        })
        .sort((a, b) => b.generationTime.getTime() - a.generationTime.getTime()),
    };
    return formatted;
  },
  getAgencyUsersInfo: async (agencyId: string): Promise<AgencyUsersInfo> => {
    const agencyUsers = (await adminApi.adminGetAgencyUsersInfo(agencyId)).body;
    const formatted: GetUsersInfoResponse = {
      ...agencyUsers,
    };
    return formatted;
  },
  getUsage: async function* (): AsyncGenerator<Dict, void, unknown> {
    for await (const agency of adminApi.adminGetUsage()) {
      yield formatUsageAgency(agency);
    }
  },
  getUsageExport: async (startDate?: string | null, endDate?: string | null): Promise<Dict[]> => {
    const resp = await adminApi.adminGetUsageExport(startDate, endDate);
    const agencies = (resp.body as any)?.agencies || [];
    return agencies.map(formatUsageAgency);
  },
  searchUsers: async (q: string): Promise<UserSearchResult[]> => {
    const resp = await adminApi.adminSearchUsers(q);
    if (resp.status !== 200 || !Array.isArray(resp.body?.users)) {
      throw new Error(`User search failed (${resp.status})`);
    }
    return resp.body.users;
  },
  getUserExport: async (
    startDate?: string | null,
    endDate?: string | null
  ): Promise<UserExportDTO[]> => {
    const resp = await adminApi.adminGetUserExport(startDate, endDate);
    // get() doesn't throw on HTTP errors; without this a 500 looks like "no users".
    if (resp.status < 200 || resp.status >= 300) {
      throw new Error(`User export failed (${resp.status})`);
    }
    return (resp.body as any)?.users || [];
  },
  getRunningWorkflows: async (
    startDate?: string | null,
    endDate?: string | null
  ): Promise<AdminWorkflowDTO[]> => {
    const resp = await adminApi.adminGetRunningWorkflows(startDate, endDate);
    const workflows = (resp.body as any)?.workflows || [];
    return workflows;
  },
  markWorkflowFailed: async (
    workflowType: string,
    workflowId: string,
    reason?: string
  ): Promise<boolean> => {
    const resp = await adminApi.adminMarkWorkflowFailed(workflowType, workflowId, reason);
    return resp.status === 200;
  },
  retryWorkflow: async (
    workflowType: string,
    workflowId: string,
    userId?: string
  ): Promise<boolean> => {
    const resp = await adminApi.adminRetryWorkflow(workflowType, workflowId, userId);
    return resp.status === 200;
  },
  getDownIndicators: async (): Promise<{ openai: boolean; anthropic: boolean }> => {
    const resp = await adminApi.adminGetDownIndicators();
    if (resp.status === 200) {
      return {
        openai: resp.body.openai ?? false,
        anthropic: resp.body.anthropic ?? false,
      };
    }
    return { openai: false, anthropic: false };
  },
  setDownIndicator: async (resource: "openai" | "anthropic", down: boolean): Promise<boolean> => {
    const resp = await adminApi.adminSetDownIndicator(resource, down);
    return resp.status === 200;
  },
  getSalesMetrics: async (
    startDate?: string,
    endDate?: string,
    rep?: string
  ): Promise<{ metrics: SalesMetricDTO[]; count: number }> => {
    const resp = await adminApi.adminGetSalesMetrics(startDate, endDate, rep);
    return {
      metrics: (resp.body as any)?.metrics || [],
      count: (resp.body as any)?.count || 0,
    };
  },
  getSalesReps: async (): Promise<string[]> => {
    const resp = await adminApi.adminGetSalesReps();
    return (resp.body as any)?.reps || [];
  },
  getIssueCategories: async (startDate?: string, endDate?: string): Promise<IssueCategoryDTO[]> => {
    const resp = await adminApi.adminGetIssueCategories(startDate, endDate);
    return (resp.body as any)?.categories || [];
  },
  getIssues: async (params?: {
    project?: string;
    category?: string;
    is_client_reported?: boolean;
    state?: string;
    start_date?: string;
    end_date?: string;
    search?: string;
  }): Promise<{ issues: IssueDTO[]; count: number }> => {
    const resp = await adminApi.adminGetIssues(params);
    return {
      issues: (resp.body as any)?.issues || [],
      count: (resp.body as any)?.count || 0,
    };
  },
  getCategoryHistory: async (): Promise<CategoryHistoryEntryDTO[]> => {
    const resp = await adminApi.adminGetCategoryHistory();
    return (resp.body as any)?.history || [];
  },
  getPrDashboard: async (refresh = false): Promise<PrDashboardResponse> => {
    const resp = await adminApi.adminGetPrDashboard(refresh);
    if (resp.status !== 200) throw new Error(resp.body.error || `HTTP ${resp.status}`);
    return resp.body;
  },
  getOperatingCosts: async (months: number, refresh = false): Promise<OperatingCostsResponse> => {
    const resp = await adminApi.adminGetOperatingCosts(months, refresh);
    if (resp.status !== 200) throw new Error(resp.body.error || `HTTP ${resp.status}`);
    return resp.body;
  },
  saveOperatingCostEntry: async (entry: OperatingCostEntry): Promise<OperatingCostEntry> => {
    const resp = await adminApi.adminSaveOperatingCostEntry(entry);
    if (resp.status !== 200) throw new Error(resp.body.error || `HTTP ${resp.status}`);
    return resp.body;
  },
  deleteOperatingCostEntry: async (id: string): Promise<void> => {
    const resp = await adminApi.adminDeleteOperatingCostEntry(id);
    if (resp.status !== 200) throw new Error(resp.body.error || `HTTP ${resp.status}`);
  },
  collectIssues: async (): Promise<CollectIssuesResponse> => {
    const resp = await adminApi.adminCollectIssues();
    return resp.body;
  },
  recalculateCategories: async (): Promise<RecalculateCategoriesResponse> => {
    const resp = await adminApi.adminRecalculateCategories();
    return resp.body;
  },
  getUsageTimeseries: async (
    params: UsageTimeseriesParams
  ): Promise<UsageTimeseriesResponseDTO> => {
    const resp = await adminApi.adminGetUsageTimeseries(params);
    return resp.body;
  },
  getUsageComparison: async (
    params: UsageComparisonParams
  ): Promise<UsageComparisonResponseDTO> => {
    const resp = await adminApi.adminGetUsageComparison(params);
    return resp.body;
  },
  getUsageOverview: async (params: UsageOverviewParams): Promise<UsageOverviewResponseDTO> => {
    const resp = await adminApi.adminGetUsageOverview(params);
    return resp.body;
  },
  getUsageIterations: async (
    params: UsageIterationsParams
  ): Promise<WorkflowIterationsResponseDTO> => {
    const resp = await adminApi.adminGetUsageIterations(params);
    return resp.body;
  },
  getPolicyInsights: async (params: PolicyInsightsParams): Promise<PolicyInsightsResponseDTO> => {
    const resp = await adminApi.adminGetPolicyInsights(params);
    return resp.body;
  },
};

export default AdminController;
